import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { CloudUpload, Preview, Refresh } from '@mui/icons-material';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { LogoCropDialog, LogoUploadResult } from '../../components/LogoCropDialog';
import { useAuth } from '../../contexts/AuthContext';
import { getLogoLayout } from '../../utils/logoLayout';

// Sedra visual constants (style-only)
const sedra = '#4a274f';
const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
  bgcolor: 'background.paper',
} as const;
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '&.Mui-focused fieldset': { borderColor: sedra },
  },
} as const;
const btnSx = { borderRadius: 2, textTransform: 'none', fontWeight: 600 } as const;

const fontOptions = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
  'Source Sans Pro',
  'Nunito',
  'Ubuntu',
  'Playfair Display'
];

export function BrandingTab() {
  const { settings, isLoading, updateSettings } = useBusinessSettings();
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [logoSuccessMessage, setLogoSuccessMessage] = useState<string | null>(null);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Track which colors have unsaved changes
  const [unsavedColors, setUnsavedColors] = useState<Set<string>>(new Set());
  const [tempColorValues, setTempColorValues] = useState<{[key: string]: string}>({});

  const handleColorChange = (field: string, color: string) => {
    // Store the color value temporarily
    setTempColorValues(prev => ({ ...prev, [field]: color }));
    setUnsavedColors(prev => new Set([...prev, field]));
  };

  const handleColorSave = async (field: string) => {
    try {
      // Get the color from our temp state
      const color = tempColorValues[field];
      
      if (!color) {
        console.error(`No color value found for ${field}`);
        return;
      }
      
      console.log(`Saving color ${field}:`, color);
      await updateSettings({ [field]: color });
      console.log(`Color ${field} saved successfully`);
      
      // Remove from unsaved changes
      setUnsavedColors(prev => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });
      
      // Clear temp value
      setTempColorValues(prev => {
        const newTemp = { ...prev };
        delete newTemp[field];
        return newTemp;
      });
      
    } catch (error) {
      console.error('Failed to update color:', error);
    }
  };

  const handleFontChange = async (font: string) => {
    try {
      await updateSettings({ font_family: font });
      // Note: Invoice preview only updates via manual refresh button
    } catch (error) {
      console.error('Failed to update font:', error);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      setSelectedFile(file);
      setShowCropDialog(true);
    }
  };

  const handleLogoSave = async (upload: LogoUploadResult) => {
    try {
      setLogoLoading(true);
      const logoLayout = getLogoLayout(upload.width, upload.height);
      console.log('Saving logo path:', upload.key);
      await updateSettings({
        logo_path: upload.key,
        logo_width: upload.width,
        logo_height: upload.height,
        logo_layout: logoLayout
      });
      console.log('Logo saved successfully');
      console.log('Updated settings:', settings);
      setSelectedFile(null);
      setLogoSuccessMessage('Logo updated successfully!');
      setTimeout(() => setLogoSuccessMessage(null), 5000); // Clear message after 5 seconds
      // Note: Invoice preview only updates via manual refresh button
    } catch (error) {
      console.error('Failed to save logo:', error);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleHtmlPdfToggle = async (enabled: boolean) => {
    try {
      await updateSettings({ use_html_pdf: enabled });
      // Note: Invoice preview only updates via manual refresh button
    } catch (error) {
      console.error('Failed to update HTML PDF setting:', error);
    }
  };

  const refreshPreview = () => {
    generatePdfPreview();
  };

  const getPreviewUrl = useMemo(() => {
    if (!currentBusiness) return '';
    const params = new URLSearchParams({
      primary_color: settings?.primary_color || '#004A99',
      secondary_color: settings?.secondary_color || '#666666',
      accent_color: settings?.accent_color || '#FF8C00',
      font_family: settings?.font_family || 'Inter',
      tax_rate: (settings?.tax_rate || 8).toString(),
      use_html_pdf: (settings?.use_html_pdf !== false).toString(),
      logo_path: settings?.logo_path || '',
      business_name: currentBusiness.name,
      business_street: currentBusiness.street || '123 Business St',
      business_city: currentBusiness.city || 'Business City',
      business_state: currentBusiness.state || 'CA',
      business_zip: currentBusiness.zip || '12345',
      business_phone: currentBusiness.phone || '(555) 123-4567',
      business_email: currentBusiness.email || 'info@samplebusiness.com',
    });
    if (settings?.business_name_font_size) {
      params.set('business_name_font_size', String(settings.business_name_font_size));
    }
    if (settings?.body_font_size) params.set('body_font_size', String(settings.body_font_size));
    if (settings?.invoice_title_font_size) params.set('invoice_title_font_size', String(settings.invoice_title_font_size));
    if (settings?.section_title_font_size) params.set('section_title_font_size', String(settings.section_title_font_size));
    if (settings?.status_badge_font_size) params.set('status_badge_font_size', String(settings.status_badge_font_size));
    if (settings?.totals_final_font_size) params.set('totals_final_font_size', String(settings.totals_final_font_size));
    if (settings?.paid_banner_font_size) params.set('paid_banner_font_size', String(settings.paid_banner_font_size));
    if (settings?.table_header_font_size) params.set('table_header_font_size', String(settings.table_header_font_size));
    if (settings?.table_cell_font_size) params.set('table_cell_font_size', String(settings.table_cell_font_size));
    if (settings?.logo_width) params.set('logo_width', String(settings.logo_width));
    if (settings?.logo_height) params.set('logo_height', String(settings.logo_height));
    if (settings?.logo_layout) params.set('logo_layout', settings.logo_layout);
    
    const base = `${API_BASE_URL}/invoices/preview-pdf`;
    const url = `${base}?${params.toString()}`;
    console.log('[BrandingTab] Preview URL constructed:', {
      logo_path: settings?.logo_path,
      fullUrl: url,
      params: Object.fromEntries(params.entries())
    });
    
    return url;
  }, [currentBusiness, settings, API_BASE_URL]);

  const generatePdfPreview = async () => {
    if (!isCookieMode && !token) {
      console.error('No authentication token available');
      return;
    }

    try {
      setPdfLoading(true);
      
      const headers: any = {};
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      // Fetch the PDF from the server
      const response = await fetch(getPreviewUrl, {
        headers,
        credentials: 'include', // Always include credentials for cross-origin requests
      });

      if (!response.ok) {
        throw new Error(`Failed to generate PDF preview: ${response.status} ${response.statusText}`);
      }

      // Convert the PDF to a blob
      const pdfBlob = await response.blob();
      
      // Create a URL for the blob
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Store the URL in state
      setPdfPreviewUrl(pdfUrl);
      
      // Clean up any previous URL to prevent memory leaks
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    } catch (error) {
      console.error('Failed to generate PDF preview:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  // Generate PDF preview when settings change (including logo)
  useEffect(() => {
    if (!isLoading && settings && (isCookieMode || token)) {
      console.log('[BrandingTab] Regenerating PDF preview due to settings change');
      generatePdfPreview();
    }
  }, [isLoading, settings, isCookieMode, token]); // Regenerate when settings change

  // Clean up PDF URL when component unmounts
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Helper function to convert S3 keys to viewable URLs
  const getLogoUrl = useCallback(async (logoPath: string | null | undefined): Promise<string | null> => {
    if (!logoPath) return null;
    
    // If it's already a local path (old system), return as-is
    if (logoPath.startsWith('/assets/logo/')) {
      return logoPath;
    }
    
    // If it's an S3 key (new system), get a presigned URL
    if (logoPath.startsWith('business/')) {
      try {
        const headers: any = {};
        
        // Only add Authorization header if not in cookie mode
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_BASE_URL}/assets/logos/view-url?key=${encodeURIComponent(logoPath)}`, {
          headers,
          credentials: 'include', // Always include credentials for cross-origin requests
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.url;
        } else {
          console.error('Failed to get logo view URL:', response.status);
          return null;
        }
      } catch (error) {
        console.error('Error getting logo view URL:', error);
        return null;
      }
    }
    
    return null;
  }, [currentBusiness?.id, isCookieMode, token, API_BASE_URL]);

  // Update resolved logo URL when settings change
  useEffect(() => {
    if (settings?.logo_path) {
      getLogoUrl(settings.logo_path).then(setResolvedLogoUrl);
    } else {
      setResolvedLogoUrl(null);
    }
  }, [settings?.logo_path, getLogoUrl]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading branding settings...</Typography>
      </Box>
    );
  }

  // Debug logging
  console.log('[BrandingTab] Current settings:', settings);
  console.log('[BrandingTab] Logo path:', settings?.logo_path);
  console.log('[BrandingTab] Has logo path:', !!settings?.logo_path);

  const renderFontSizeField = (
    field: string,
    label: string,
    defaultPx: number
  ) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label} (px)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          type="number"
          size="small"
          inputProps={{ step: 1 }}
          value={(tempColorValues as any)[field] ?? String((settings as any)?.[field] ?? defaultPx)}
          onChange={(e) => {
            const raw = e.target.value;
            (setTempColorValues as any)((prev:any) => ({ ...prev, [field]: raw }));
            setUnsavedColors(prev => new Set([...prev, field]));
          }}
          sx={{ width: 140, ...inputSx }}
        />
        <Button
          size="small"
          variant={unsavedColors.has(field) ? 'contained' : 'outlined'}
          onClick={async () => {
            const raw = (tempColorValues as any)[field];
            let val = raw !== undefined ? parseInt(String(raw), 10) : ((settings as any)?.[field] ?? defaultPx);
            if (isNaN(val)) val = (settings as any)?.[field] ?? defaultPx;
            val = Math.max(6, Math.min(200, val));
            await updateSettings({ [field]: val } as any);
            setUnsavedColors(prev => { const next = new Set(prev); next.delete(field); return next; });
            (setTempColorValues as any)((prev:any) => { const cp={...prev}; delete cp[field]; return cp; });
          }}
          sx={btnSx}
        >
          Save
        </Button>
      </Box>
    </Box>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      {/* LEFT: controls (2 cols -> now 7/12) */}
      <div className="lg:col-span-7 space-y-6">
        <Card elevation={0} sx={cardSx} className="rounded-2xl">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Invoice Customization
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Primary Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  border: unsavedColors.has('primary_color') ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  p: 0.5
                }}>
                  <input
                    type="color"
                    value={tempColorValues['primary_color'] || settings?.primary_color || '#004A99'}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    data-field="primary_color"
                    style={{ 
                      width: 60, 
                      height: 40, 
                      border: 'none', 
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  {unsavedColors.has('primary_color') && (
                    <Button 
                      size="small" 
                      onClick={() => handleColorSave('primary_color')} 
                      variant="contained"
                      sx={{ 
                        position: 'absolute', 
                        right: -6, 
                        top: -6, 
                        minWidth: 'auto',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        fontSize: '0.7rem',
                        p: 0,
                        zIndex: 1
                      }}
                    >
                      ✓
                    </Button>
                  )}
                </Box>
                <TextField
                  value={tempColorValues['primary_color'] || settings?.primary_color || '#004A99'}
                  onChange={(e) => handleColorChange('primary_color', e.target.value)}
                  size="small"
                  sx={{ flex: 1, ...inputSx }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Secondary Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  border: unsavedColors.has('secondary_color') ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  p: 0.5
                }}>
                  <input
                    type="color"
                    value={tempColorValues['secondary_color'] || settings?.secondary_color || '#666666'}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    data-field="secondary_color"
                    style={{ 
                      width: 60, 
                      height: 40, 
                      border: 'none', 
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  {unsavedColors.has('secondary_color') && (
                    <Button 
                      size="small" 
                      onClick={() => handleColorSave('secondary_color')} 
                      variant="contained"
                      sx={{ 
                        position: 'absolute', 
                        right: -6, 
                        top: -6, 
                        minWidth: 'auto',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        fontSize: '0.7rem',
                        p: 0,
                        zIndex: 1
                      }}
                    >
                      ✓
                    </Button>
                  )}
                </Box>
                <TextField
                  value={tempColorValues['secondary_color'] || settings?.secondary_color || '#666666'}
                  onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                  size="small"
                  sx={{ flex: 1, ...inputSx }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Accent Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  border: unsavedColors.has('accent_color') ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  p: 0.5
                }}>
                  <input
                    type="color"
                    value={tempColorValues['accent_color'] || settings?.accent_color || '#FF8C00'}
                    onChange={(e) => handleColorChange('accent_color', e.target.value)}
                    data-field="accent_color"
                    style={{ 
                      width: 60, 
                      height: 40, 
                      border: 'none', 
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  {unsavedColors.has('accent_color') && (
                    <Button 
                      size="small" 
                      onClick={() => handleColorSave('accent_color')} 
                      variant="contained"
                      sx={{ 
                        position: 'absolute', 
                        right: -6, 
                        top: -6, 
                        minWidth: 'auto',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        fontSize: '0.7rem',
                        p: 0,
                        zIndex: 1
                      }}
                    >
                      ✓
                    </Button>
                  )}
                </Box>
                <TextField
                  value={tempColorValues['accent_color'] || settings?.accent_color || '#FF8C00'}
                  onChange={(e) => handleColorChange('accent_color', e.target.value)}
                  size="small"
                  sx={{ flex: 1, ...inputSx }}
                />
              </Box>
            </Box>

            {/* Font sizes */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {renderFontSizeField('business_name_font_size', 'Company Name Font Size', 26)}
              {renderFontSizeField('body_font_size', 'Body Font Size', 13)}
              {renderFontSizeField('invoice_title_font_size', 'Invoice Title Font Size', 30)}
              {renderFontSizeField('section_title_font_size', 'Section Title Font Size', 15)}
              {renderFontSizeField('status_badge_font_size', 'Status Badge Font Size', 11)}
              {renderFontSizeField('totals_final_font_size', 'Totals Row Font Size', 16)}
              {renderFontSizeField('paid_banner_font_size', 'Paid Banner Font Size', 24)}
              {renderFontSizeField('table_header_font_size', 'Table Header Font Size', 12)}
              {renderFontSizeField('table_cell_font_size', 'Table Cell Font Size', 13)}
            </Box>

          </CardContent>
        </Card>

        {/* Font and Logo Settings */}
        <Card elevation={0} sx={cardSx} className="rounded-2xl">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Typography & Logo
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Font Family
              </Typography>
              <TextField
                select
                fullWidth
                value={settings?.font_family || 'Inter'}
                onChange={(e) => handleFontChange(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                sx={inputSx}
              >
                {fontOptions.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Logo
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                {settings?.logo_path ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      border: '1px solid #ddd', 
                      borderRadius: 1, 
                      p: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      minHeight: 60,
                      minWidth: 220,
                      maxWidth: 260
                    }}>
                      {logoLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <img
                          key={settings.logo_path}
                          src={resolvedLogoUrl || settings.logo_path}
                          alt="Business Logo"
                          style={{ 
                            maxHeight: 48,
                            maxWidth: 220,
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            console.error('Failed to load logo:', e);
                            console.error('Logo path was:', settings.logo_path);
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('Logo loaded successfully:', settings.logo_path);
                          }}
                        />
                      )}
                    </Box>
                    <Button
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      startIcon={<CloudUpload />}
                      disabled={logoLoading}
                      sx={btnSx}
                    >
                      Replace
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<CloudUpload />}
                    fullWidth
                    disabled={logoLoading}
                    sx={btnSx}
                  >
                    Upload Logo
                  </Button>
                )}

                <Typography variant="caption" color="text.secondary">
                  Logo proportions are preserved in invoices and top-left envelope return blocks
                </Typography>
                
                {/* Logo Preview Section - Always Visible */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Logo Preview:
                  </Typography>
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 2, 
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 90,
                    width: 260,
                    maxWidth: '100%'
                  }}>
                    {logoLoading ? (
                      <CircularProgress size={16} />
                    ) : resolvedLogoUrl ? (
                      <img
                        key={resolvedLogoUrl}
                        src={resolvedLogoUrl}
                        alt="Logo Preview"
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error('Failed to load logo preview:', e);
                          console.error('Logo path was:', resolvedLogoUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('Logo preview loaded successfully');
                          console.log('Logo path:', resolvedLogoUrl);
                        }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: 60, 
                        height: 60, 
                        bgcolor: '#f5f5f5', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderRadius: 1,
                        border: '1px dashed #ccc'
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          No Logo
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="h6" gutterBottom>
                Invoice features
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings?.enable_invoice_balance_forward}
                    onChange={async (e) => {
                      try {
                        await updateSettings({ enable_invoice_balance_forward: e.target.checked });
                      } catch (err) {
                        console.error('Failed to update balance forward setting', err);
                      }
                    }}
                  />
                }
                label="Show Account summary (Balance forward) on invoices"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                PDF generation
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.use_html_pdf !== false}
                    onChange={(e) => handleHtmlPdfToggle(e.target.checked)}
                  />
                }
                label="Use HTML PDF Generation (Enhanced styling)"
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                When enabled, invoices will use the new HTML-based PDF generation with custom branding.
                Disable to use the legacy PDFKit generation.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>

      {/* RIGHT: Preview (1 col -> now 5/12) */}
      <div className="lg:col-span-5">
        <Card elevation={0} sx={cardSx} className="rounded-2xl">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Invoice Preview
              </Typography>
              <Tooltip title="Refresh Preview">
                <IconButton onClick={refreshPreview}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
              className="rounded-xl"
            >
              {pdfLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">
                    Generating PDF preview...
                  </Typography>
                </Box>
              ) : pdfPreviewUrl ? (
                <iframe
                  src={pdfPreviewUrl}
                  style={{ width: '100%', height: 750, border: 'none' }}
                  title="Invoice Preview"
                />
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    PDF preview not available. 
                    <Button 
                      size="small" 
                      onClick={generatePdfPreview}
                      sx={{ ml: 1 }}
                    >
                      Generate Preview
                    </Button>
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {logoSuccessMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {logoSuccessMessage}
          </Alert>
        )}
      </div>

      <LogoCropDialog
        open={showCropDialog}
        onClose={() => setShowCropDialog(false)}
        onSave={handleLogoSave}
        imageFile={selectedFile!}
      />
    </div>
  );
} 
